import React, { forwardRef, useCallback } from 'react';
import CompletionsEditableTable, { ColumnConfig, EditableTableRef } from './CompletionsEditableTable';
import { BoreData } from '../../../../models/CompletionModels';
import { mPFCorrelations, flowOpts } from './CompletionDropdownData';
import { useAppSelector } from '../../../../hooks/storeHooks';
import { fetchDefaultBore } from '../../../../services/well-completions/WellCompletionsService';

interface CompletionsBoresTabProps {
  data?: BoreData[];
}

const NUMERIC_FIELD_VALIDATIONS = [
  { field: 'DepthTrueVertical', label: 'Depth TVD' },
  { field: 'DepthMeasured', label: 'Depth MD' },
  { field: 'CasingId', label: 'Casing ID' },
  { field: 'TubingId', label: 'Tubing ID' },
  { field: 'Roughness', label: 'Roughness' },
] as const;

const CompletionsBoresTab = forwardRef<EditableTableRef, CompletionsBoresTabProps>(({ data }, ref) => {
  const defaultBoreData = useAppSelector((state: any) => state.CreateCompletions.defaultBore.data);
  const defaultBoreLoading = useAppSelector((state: any) => state.CreateCompletions.defaultBore.loading);

  const validateBoreData = useCallback((rowData: BoreData): string[] => {
    const errors: string[] = [];

    for (const { field, label } of NUMERIC_FIELD_VALIDATIONS) {
      const value = rowData[field];
      if (value <= 0) {
        errors.push(`${label} - Value must be greater than 0`);
      }
    }

    if (
      rowData.DepthMeasured !== undefined &&
      rowData.DepthTrueVertical !== undefined &&
      rowData.DepthMeasured <= rowData.DepthTrueVertical
    ) {
      errors.push('Depth MD must be greater than Depth TVD');
    }

    return errors;
  }, []);

  const boreColumns: ColumnConfig[] = [
    { key: 'DepthTrueVertical', header: 'Depth TVD', type: 'input', isNumeric: true },
    { key: 'DepthMeasured', header: 'Depth MD', type: 'input', isNumeric: true },
    { key: 'CasingId', header: 'Casing ID', type: 'input', isNumeric: true },
    { key: 'TubingOd', header: 'Tubing OD', type: 'input', isNumeric: true },
    { key: 'TubingId', header: 'Tubing ID', type: 'input', isNumeric: true },
    { key: 'Roughness', header: 'Roughness', type: 'input', isNumeric: true },
    { key: 'Correlation', header: 'MPF Correlation', type: 'select', options: mPFCorrelations },
    { key: 'FlowOpt', header: 'Flow Path', type: 'select', options: flowOpts },
  ];

  const createNewBoreRow = (newId: number, defaultData?: any): BoreData => {
    const defaultValues = defaultData || {};

    return {
      Id: newId,
      DepthTrueVertical: defaultValues.DepthTrueVertical || 0,
      DepthMeasured: defaultValues.DepthMeasured || 0,
      CasingId: defaultValues.CasingId || 0,
      TubingOd: defaultValues.TubingOd || 0,
      TubingId: defaultValues.TubingId || 0,
      Roughness: defaultValues.Roughness || 0,
      Correlation: defaultValues?.Correlation ?? mPFCorrelations[0]?.key ?? 0,
      FlowOpt: defaultValues?.FlowOpt ?? flowOpts[0]?.key ?? 0,
    };
  };

  return (
    <CompletionsEditableTable
      ref={ref}
      title='Bores'
      data={data}
      columns={boreColumns}
      onFetchDefault={() => fetchDefaultBore()}
      defaultData={defaultBoreData}
      defaultLoading={defaultBoreLoading}
      createNewRow={createNewBoreRow}
      className='completions-bore-tab-table'
      validateRow={validateBoreData}
    />
  );
});

export default CompletionsBoresTab;
